import { useState } from 'react';
import { call } from '../api';
import { cycleRepeat, fmtTime, jumpTo, next, prev, seek, setFull, setVolume, stopMusic, storable, toggle, toggleShuffle, useFullPlayer, useMusic } from '../musicPlayer';
import { linkProps } from '../router';

/** Balk onderin zolang er muziek is: bediening, tijd, wachtrij en volume. */
export function MiniPlayer() {
  const s = useMusic();
  const [queueOpen, setQueueOpen] = useState(false);
  const [fav, setFav] = useState<Record<string, boolean>>({});
  const isFull = useFullPlayer();
  const t = s.queue[s.index];
  if (!t || isFull) return null;
  const dur = s.duration || t.duration || 0;
  const favKey = `${t.source}:${t.addonId ?? ''}:${t.id}`;
  const isFav = fav[favKey];

  const toggleFav = async () => {
    const on = !isFav;
    setFav((f) => ({ ...f, [favKey]: on }));
    await call('/api/music/favorites', { method: 'PUT', body: JSON.stringify({ track: storable(t), on }) }).catch(() => setFav((f) => ({ ...f, [favKey]: !on })));
  };

  return (
    <div className="mini-player" role="region" aria-label="Muziekspeler">
      <div className="mp-progress">
        <input type="range" min={0} max={Math.max(1, dur)} step={1} value={Math.min(s.time, dur)} onChange={(e) => seek(Number(e.target.value))} aria-label="Positie in nummer" />
      </div>
      <div className="mp-row">
        <button type="button" className="mp-art mp-art-btn" style={t.artwork ? { backgroundImage: `url("${t.artwork}")` } : undefined} onClick={() => setFull(true)} aria-label="Volledige speler openen" title="Volledige speler" />
        <a className="mp-track" {...linkProps('/music')}>
          <span className="mp-text">
            <strong>{t.title}</strong>
            <span className="subtle">{t.artist}{t.addonName ? ` · via ${t.addonName}` : t.source === 'plex' ? ' · Plex' : ''}</span>
          </span>
        </a>
        <button className={`mp-btn${isFav ? ' on' : ''}`} onClick={() => void toggleFav()} aria-label={isFav ? 'Uit favorieten' : 'Aan favorieten toevoegen'} aria-pressed={!!isFav}>{isFav ? '♥' : '♡'}</button>
        <div className="mp-controls" data-focus-group>
          <button className={`mp-btn${s.shuffle ? ' on' : ''}`} onClick={toggleShuffle} aria-pressed={s.shuffle} aria-label="Shuffle">⤮</button>
          <button className="mp-btn" onClick={prev} aria-label="Vorige">⏮</button>
          <button className="mp-btn mp-play" onClick={toggle} aria-label={s.playing ? 'Pauze' : 'Afspelen'}>{s.loading ? '…' : s.playing ? '❚❚' : '▶'}</button>
          <button className="mp-btn" onClick={() => next()} aria-label="Volgende">⏭</button>
          <button className={`mp-btn${s.repeat !== 'off' ? ' on' : ''}`} onClick={cycleRepeat} aria-label={`Herhalen: ${s.repeat === 'off' ? 'uit' : s.repeat === 'all' ? 'alles' : 'dit nummer'}`}>{s.repeat === 'one' ? '↻1' : '↻'}</button>
        </div>
        <span className="mp-time subtle">{fmtTime(s.time)} / {fmtTime(dur)}</span>
        {s.quality && <span className="mp-quality">{s.quality}</span>}
        {s.error && <span className="error mp-error" role="alert">{s.error}</span>}
        <div className="mp-right">
          <button className={`mp-btn${queueOpen ? ' on' : ''}`} onClick={() => setQueueOpen((v) => !v)} aria-expanded={queueOpen} aria-label="Wachtrij">☰</button>
          <button className="mp-btn" onClick={() => setFull(true)} aria-label="Volledige speler openen">⤢</button>
          <input className="mp-volume" type="range" min={0} max={1} step={0.02} value={s.volume} onChange={(e) => setVolume(Number(e.target.value))} aria-label="Volume" />
          <button className="mp-btn" onClick={stopMusic} aria-label="Stoppen en sluiten">✕</button>
        </div>
      </div>
      {queueOpen && (
        <ol className="mp-queue" aria-label="Wachtrij">
          {s.queue.map((q, i) => (
            <li key={`${q.id}:${i}`}>
              <button className="mp-q-item" aria-current={i === s.index ? 'true' : undefined} onClick={() => jumpTo(i)}>
                <span className="mp-q-title">{q.title}</span>
                <span className="subtle">{q.artist}</span>
                <span className="subtle">{fmtTime(q.duration)}</span>
              </button>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
